/** 남은 원본 보기 · 지우기, 판으로 되돌리기의 웹 클라이언트와 화면 문장(`W-50` · `W-55`).
 *
 * 경로와 응답 모양은 shared `originals-api.ts` 에서만 온다(라우트 `apps/api/src/routes/originals.ts`,
 * `docs/DATA_DICTIONARY.md` 51장). 화면 문장은 코드 값으로 고른다. 서버 메시지를 그대로 쓰지 않는다.
 * 문장의 정본은 2026-10-25 `design-manager` 자문이고, 낱말은 PRD `7.5.9` 용어 표를 따른다.
 *
 * 요청은 전부 `api.ts` 의 `request` · `send` 를 거친다. **401 이면 세션 게이트가 깨어난다**(로그인으로 넘어가므로
 * 아래 문장 함수는 401 에 `null` 을 준다. 알림을 띄우지 않는다).
 */

import {
  CONTACT_EMAIL,
  REVERT_REFUSED_COPY,
  originalsEndpoints,
  type DeleteOriginalsResponse,
  type JobOriginalsView,
  type OriginalsBlockedCode,
  type OriginalsDeleteResultItem,
  type OriginalsSummaryView,
  type RevertChangedPart,
  type RevertJobResponse,
} from "@makit/shared";

import { ApiError, readErrorCode, request, send } from "./api";

// ── 요청 ─────────────────────────────────────────────────────────────────────

function postJson(body?: unknown): RequestInit {
  return body === undefined
    ? { method: "POST" }
    : { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) };
}

/** 작업 하나의 원본 현황. 장이 없는 작업도 0장으로 온다. */
export function fetchJobOriginals(jobId: string): Promise<JobOriginalsView> {
  return request<JobOriginalsView>(originalsEndpoints.job(jobId));
}

/** 셀러의 원본 현황. **원본이 한 장이라도 남은 작업만** 온다. 서버 상한에 걸리면 `truncated`. */
export function fetchOriginalsSummary(): Promise<OriginalsSummaryView> {
  return request<OriginalsSummaryView>(originalsEndpoints.summary());
}

/** 작업 하나의 원본 지우기.
 *
 * 막힘(409)은 `ApiError(409, code)` 로 던진다. **R2 삭제가 실패한 500 은 본문이 결과다** —
 * `outcome: "FAILED"` 와 이번에 지운 장 수(`deletedCount`)가 실려 오므로 던지지 않고 돌려준다.
 * 일부만 지운 것을 「못 지웠다」로 뭉개지 않기 위해서다. 본문을 못 읽으면 그때만 던진다. */
export async function deleteJobOriginals(jobId: string): Promise<DeleteOriginalsResponse> {
  const res = await send(originalsEndpoints.deleteJob(jobId), postJson());
  if (res.ok) return (await res.json()) as DeleteOriginalsResponse;
  if (res.status === 500) {
    try {
      const body = (await res.json()) as Partial<DeleteOriginalsResponse>;
      if (Array.isArray(body.items) && body.items.length > 0) return body as DeleteOriginalsResponse;
    } catch {
      // 아래에서 던진다.
    }
    throw new ApiError(500);
  }
  throw new ApiError(res.status, await readErrorCode(res));
}

/** 고른 작업들의 원본 지우기. **언제나 200 이고 작업마다 결과가 온다**(일부 성공을 실패로 뭉개지 않는다).
 *  ⛔ 상한(`ORIGINALS_DELETE_MAX_JOBS`)을 넘겨 부르지 않는다. `W-55` 는 한 페이지(`PAGE_SIZE` ≤ 상한)만 고른다. */
export function deleteOriginals(jobIds: readonly string[]): Promise<DeleteOriginalsResponse> {
  return request<DeleteOriginalsResponse>(originalsEndpoints.deleteMany(), postJson({ jobIds: [...jobIds] }));
}

/** 그 판으로 되돌리기. **새 판을 만들지 않는다**(DATA_DICTIONARY 51.2). 거절은 `ApiError(409|422, code)`. */
export function revertToVersion(jobId: string, versionId: string): Promise<RevertJobResponse> {
  return request<RevertJobResponse>(originalsEndpoints.revert(jobId, versionId), postJson());
}

// ── 문장: 원본 줄 (`W-50`) ──────────────────────────────────────────────────

/** 지울 수 없는 사유. `W-50` 의 비활성 사유와 409 거절 알림이 같은 문장을 쓴다. */
export const ORIGINALS_BLOCKED_REASON: Record<OriginalsBlockedCode, string> = {
  ALREADY_PROCESSING: "지금 만드는 중이라 잠시 뒤에 지울 수 있습니다.",
  PAYMENT_IN_PROGRESS: "결제를 확인하는 중이라 지금은 지울 수 없습니다. 잠시 뒤 다시 눌러 주세요.",
  IMAGES_UNSETTLED:
    "결제하신 장이 아직 남았습니다. 남은 장을 만들려면 원본 사진이 있어야 해서 지금은 지울 수 없습니다.",
};

function isBlockedCode(code: string | undefined): code is OriginalsBlockedCode {
  return code !== undefined && code in ORIGINALS_BLOCKED_REASON;
}

const CANNOT_REMAKE = "원본이 없어지면 다시 만들 수 없습니다";

/** `W-50` 원본 줄 한 칸. 목업 `원본 8장 · 배경 지운 7장` / `올린 날부터 30일 · 22일 남음 · …`. */
export interface OriginalsLine {
  name: string;
  sub: string;
  /** 남은 원본이 없으면 `지우기` 를 **숨긴다**(꺼 두면 「어떻게 켜나」를 찾게 된다). */
  showDelete: boolean;
  /** 지금 지울 수 없으면 그 사유. */
  blockedReason: string | null;
}

/** `cutouts` 는 배경 지운 장 수다(셸이 이미 들고 있는 값). 남은 날은 서버가 셈한 `daysLeft` 그대로 쓴다.
 *
 * 원본을 한 장도 받은 적이 없는 작업(남은 0 · 없어진 0)은 `null` 이다. 줄을 그리지 않는다 —
 * 「원본이 없어져」는 없어진 적이 없는 원본에 대해 거짓이다. */
export function originalsLine(view: JobOriginalsView, cutouts: number): OriginalsLine | null {
  if (view.originalCount === 0 && view.purgedCount === 0) return null;
  if (view.originalCount === 0) {
    return {
      name: `원본 없음 · 배경 지운 ${cutouts}장`,
      sub: "원본이 없어져 다시 만들 수 없습니다",
      showDelete: false,
      blockedReason: null,
    };
  }
  const days =
    view.daysLeft === null ? null : view.daysLeft === 0 ? "하루 안에 없어집니다" : `${view.daysLeft}일 남음`;
  const tail =
    view.purgedCount > 0 ? `원본이 없어진 ${view.purgedCount}장은 이미 다시 만들 수 없습니다` : CANNOT_REMAKE;
  return {
    name: `원본 ${view.originalCount}장 · 배경 지운 ${cutouts}장`,
    sub: ["올린 날부터 30일", days, tail].filter(Boolean).join(" · "),
    showDelete: true,
    blockedReason: view.deleteBlockedBy ? ORIGINALS_BLOCKED_REASON[view.deleteBlockedBy] : null,
  };
}

// ── 문장: 지우기 결과 ────────────────────────────────────────────────────────

const NOT_SURE_DELETED = "지웠는지 확인하지 못했습니다. 연결을 확인하고 이 화면을 다시 열어 주세요.";
const DELETE_FAILED = "원본 사진을 지우지 못했습니다. 지우기를 다시 눌러 주세요.";
/** 서버가 분명히 받지 않은 요청(4xx). 다시 눌러도 같으므로 다시 누르라고 하지 않는다(2026-10-25 design-manager). */
const DELETE_NOT_FOUND = "찾을 수 없는 작업이라 원본 사진을 지우지 못했습니다.";
const DELETE_REFUSED = `원본 사진을 지우지 못했습니다. ${CONTACT_EMAIL}로 알려 주세요.`;
const BULK_DELETE_REFUSED = "원본 사진을 하나도 지우지 못했습니다. 이 화면을 다시 열고 작업을 다시 골라 주세요.";

/** 응답이 없거나(0) 서버가 넘어졌으면(5xx) 했는지 **모른다**. 이 둘에만 「확인하지 못했습니다」를 쓴다. */
function unknownOutcome(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
}

/** 401 은 세션 게이트가 로그인으로 넘긴다(`api.ts` 의 `send`). 알림을 따로 띄우지 않는다. */
function signedOut(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/** `W-50` 한 작업 지우기의 결과 알림. */
export function deleteResultMessage(res: DeleteOriginalsResponse): string {
  const item = res.items[0];
  if (!item) return DELETE_FAILED;
  switch (item.outcome) {
    case "DELETED":
      return `원본 사진 ${item.deletedCount}장을 지웠습니다.`;
    case "NOTHING_TO_DELETE":
      return "원본 사진이 이미 없습니다.";
    case "FAILED":
      return item.deletedCount > 0
        ? `원본 사진 ${item.deletedCount}장을 지웠지만 일부는 지우지 못했습니다. 지우기를 다시 눌러 주세요.`
        : DELETE_FAILED;
    default:
      return isBlockedCode(item.outcome) ? ORIGINALS_BLOCKED_REASON[item.outcome] : DELETE_FAILED;
  }
}

/** 한 작업 지우기가 던진 것의 문장. `null` 이면 알림을 띄우지 않는다(401: 로그인으로 넘어간다).
 *  응답이 없거나 5xx 면 **지웠는지 모른다**고 말한다. 막힘(409)은 사유, 404 · 그 밖의 4xx 는 「지우지 못했다」. */
export function deleteErrorMessage(error: unknown): string | null {
  if (signedOut(error)) return null;
  if (unknownOutcome(error)) return NOT_SURE_DELETED;
  const e = error as ApiError;
  if (isBlockedCode(e.code)) return ORIGINALS_BLOCKED_REASON[e.code];
  if (e.status === 404) return DELETE_NOT_FOUND;
  return DELETE_REFUSED;
}

/** 고른 작업 지우기가 던진 것의 문장. 이 라우트는 작업마다 결과를 200 으로 주므로, 던졌다면 요청 자체가
 *  안 들어간 것(4xx: 아무것도 지우지 않음)이거나 응답을 모르는 것(0 · 5xx)이다. */
export function bulkDeleteErrorMessage(error: unknown): string | null {
  if (signedOut(error)) return null;
  return unknownOutcome(error) ? NOT_SURE_DELETED : BULK_DELETE_REFUSED;
}

// ── 문장: 고른 작업 지우기 (`W-55`) ──────────────────────────────────────────

/** 작업 한 줄의 결과 꼬리(행 `sd` 끝에 ` · ` 다음). 다 지운 작업은 꼬리가 없다(`null`). */
export function bulkDeleteRowTail(item: OriginalsDeleteResultItem): string | null {
  switch (item.outcome) {
    case "DELETED":
      return null;
    case "NOTHING_TO_DELETE":
      return "원본이 이미 없어요";
    case "ALREADY_PROCESSING":
      return "만드는 중이라 지우지 못했어요";
    case "PAYMENT_IN_PROGRESS":
      return "결제를 확인하는 중이라 지우지 못했어요";
    case "IMAGES_UNSETTLED":
      return "결제하신 장이 남아 지우지 못했어요";
    case "NOT_FOUND":
      return "찾을 수 없는 작업이라 지우지 못했어요";
    case "FAILED":
      return item.deletedCount > 0 ? `${item.deletedCount}장만 지웠어요` : "지우지 못했어요";
  }
}

/** 지우지 못한 작업인가. 이미 없던 작업(`NOTHING_TO_DELETE`)은 실패가 아니다. */
export function bulkDeleteFailed(item: OriginalsDeleteResultItem): boolean {
  return item.outcome !== "DELETED" && item.outcome !== "NOTHING_TO_DELETE";
}

/** 요약 한 줄. 하나라도 못 지웠으면 다음 동작까지 남는 배너(`banner`), 아니면 알림(`toast`). */
export function bulkDeleteSummary(res: DeleteOriginalsResponse): { kind: "toast" | "banner"; message: string } {
  // `n건` 은 원본 사진을 실제로 지운 작업 수다(일부만 지운 작업도 든다). 장 수는 서버 합계를 쓴다.
  const touched = res.items.filter((i) => i.deletedCount > 0).length;
  const head = `${touched}건에서 원본 사진 ${res.deletedCount}장을 지웠습니다.`;
  const failed = res.items.filter(bulkDeleteFailed).length;
  if (failed > 0) return { kind: "banner", message: `${head} ${failed}건은 지우지 못해 그 줄에 사유를 적었습니다.` };
  const already = res.items.filter((i) => i.outcome === "NOTHING_TO_DELETE").length;
  return {
    kind: "toast",
    message: already > 0 ? `${head} ${already}건은 원본 사진이 이미 없었습니다.` : head,
  };
}

// ── 문장: 되돌리기 ──────────────────────────────────────────────────────────

/** 되돌리기가 바꾸는 다섯 축의 이름. **순서는 `W-30` 의 순서다.** `SPEC` 은 `스펙표 내용` 이다
 *  (`스펙표` 만 쓰면 블록 하나를 켜고 끄는 `블록 구성` 과 섞인다). */
export const REVERT_PART_WORD: Record<RevertChangedPart, string> = {
  PHOTO_ORDER: "사진 순서",
  PRODUCT_NAME: "상품명",
  BLOCKS: "블록 구성",
  BACKGROUND: "배경",
  SPEC: "스펙표 내용",
};

const PART_ORDER: RevertChangedPart[] = ["PHOTO_ORDER", "PRODUCT_NAME", "BLOCKS", "BACKGROUND", "SPEC"];

/** 다섯 축 전부. 확인 창과 판 아래 안내가 이 한 줄을 쓴다. */
export const REVERT_ALL_PARTS = PART_ORDER.map((p) => REVERT_PART_WORD[p]).join(" · ");

/** 되돌리기 결과 알림. 미리보기가 없으니 **무엇이 바뀌었는지 말하는 자리가 여기 하나다.** */
export function revertResultMessage(versionName: string, res: RevertJobResponse): string {
  if (!res.reverted || res.changed.length === 0) return `이미 ${versionName} 판과 같아 바뀐 것이 없습니다.`;
  const parts = PART_ORDER.filter((p) => res.changed.includes(p)).map((p) => REVERT_PART_WORD[p]);
  return `${versionName} 판으로 되돌렸습니다. 바뀐 것은 ${parts.join(" · ")}입니다.`;
}

/** 거절 문장 다섯은 앱과 한 벌이다(`@makit/shared` `REVERT_REFUSED_COPY`, PRD `7.13` D-97). */
const REVERT_REFUSED: Record<string, string> = REVERT_REFUSED_COPY;

const NOT_SURE_REVERTED = "되돌렸는지 확인하지 못했습니다. 연결을 확인하고 이 화면을 다시 열어 주세요.";
const REVERT_NOT_FOUND = "찾을 수 없는 판이라 되돌리지 못했습니다.";
const REVERT_REFUSED_OTHER = `이 판으로 되돌리지 못했습니다. ${CONTACT_EMAIL}로 알려 주세요.`;

/** 되돌리기가 던진 것의 문장. `null` 이면 알림을 띄우지 않는다(401). 거절 코드는 그 사유, 404 는 「찾을 수
 *  없는 판」, 응답이 없거나 5xx(저장 실패)면 **되돌렸는지 모른다**, 코드 없는 그 밖의 4xx 는 「되돌리지 못했다」. */
export function revertErrorMessage(error: unknown): string | null {
  if (signedOut(error)) return null;
  if (error instanceof ApiError && error.code && REVERT_REFUSED[error.code]) return REVERT_REFUSED[error.code]!;
  if (unknownOutcome(error)) return NOT_SURE_REVERTED;
  if ((error as ApiError).status === 404) return REVERT_NOT_FOUND;
  return REVERT_REFUSED_OTHER;
}
